// Thin progress bar for long admin actions (CSV import, batch face upload).
//
// Two modes:
//   - determinate: pass `value` in [0,1] → fill width tracks it.
//   - indeterminate: omit `value` (or pass undefined) → a sliver slides across,
//     for work whose progress we can't observe (e.g. a single POST the server
//     processes opaquely). We never fake a determinate percentage.
//
// Tailwind admin theme tokens only; the indeterminate keyframe `progress-slide`
// lives in src/index.css.

export interface ProgressBarProps {
  /** 0..1 for determinate. Omit for an indeterminate sliding bar. */
  value?: number;
  /** Optional caption shown above the bar (already-translated string). */
  label?: string;
}

export function ProgressBar({ value, label }: ProgressBarProps) {
  const determinate = typeof value === "number" && !Number.isNaN(value);
  const pct = determinate ? Math.max(0, Math.min(1, value)) * 100 : 0;

  return (
    <div>
      {label ? (
        <div className="mb-1 flex justify-between text-xs text-admin-text3">
          <span>{label}</span>
          {determinate ? <span>{Math.round(pct)}%</span> : null}
        </div>
      ) : null}
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-admin-bg3"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={determinate ? Math.round(pct) : undefined}
      >
        {determinate ? (
          <div
            className="h-full rounded-full bg-admin-cyan transition-[width] duration-200"
            style={{ width: `${pct}%` }}
          />
        ) : (
          <div className="h-full w-1/3 rounded-full bg-admin-cyan animate-[progress-slide_1.1s_ease-in-out_infinite]" />
        )}
      </div>
    </div>
  );
}
